import { useState } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark, oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Button, useThemeContext } from '@mieweb/ui';

// Override oneDark colors for WCAG AA contrast (≥4.5:1 on #282c34 background)
const oneDarkAccessible: typeof oneDark = {
  ...oneDark,
  // Comments: hsl(220,10%,40%) → #8b949e  (was ~2.3:1, now ~5.1:1)
  'comment': { ...oneDark['comment'], color: '#8b949e' },
  'prolog': { ...oneDark['prolog'], color: '#8b949e' },
  'cdata': { ...oneDark['cdata'], color: '#8b949e' },
  // Tags/symbols: hsl(355,65%,65%) → hsl(355,65%,72%)  (was ~4.4:1, now ~5.7:1)
  'property': { ...oneDark['property'], color: 'hsl(355, 65%, 72%)' },
  'tag': { ...oneDark['tag'], color: 'hsl(355, 65%, 72%)' },
  'symbol': { ...oneDark['symbol'], color: 'hsl(355, 65%, 72%)' },
  'deleted': { ...oneDark['deleted'], color: 'hsl(355, 65%, 72%)' },
  'important': { ...oneDark['important'], color: 'hsl(355, 65%, 72%)' },
};

type Language = 
  | 'javascript' 
  | 'typescript' 
  | 'jsx' 
  | 'tsx' 
  | 'html' 
  | 'css' 
  | 'json' 
  | 'bash' 
  | 'shell'
  | 'python'
  | 'curl'
  | 'text'
  | 'nginx'
  | 'apache';

interface CodeBlockProps {
  code: string;
  language?: Language;
  showCopy?: boolean;
  showLineNumbers?: boolean;
  theme?: 'dark' | 'light' | 'auto';
  label?: string;
  className?: string;
}

export function CodeBlock({ 
  code, 
  language = 'javascript',
  showCopy = true,
  showLineNumbers = false,
  theme = 'auto',
  label,
  className = '',
}: CodeBlockProps) {
  const [copied, setCopied] = useState(false);
  const { resolvedTheme } = useThemeContext();

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Map 'curl' to 'bash' for syntax highlighting
  const highlightLanguage = language === 'curl' ? 'bash' : language;
  
  const effectiveTheme = theme === 'auto' ? (resolvedTheme === 'dark' ? 'dark' : 'light') : theme;
  const syntaxTheme = effectiveTheme === 'dark' ? oneDarkAccessible : oneLight;

  return (
    <div className={`relative rounded-xl overflow-hidden ${className}`}>
      {label && (
        <div className={`px-4 py-2 text-xs font-mono ${
          effectiveTheme === 'dark' 
            ? 'bg-slate-800 text-slate-400 border-b border-slate-700' 
            : 'bg-slate-100 text-slate-600 border-b border-slate-200'
        }`}>
          {label}
        </div>
      )}
      <div className="relative">
        <SyntaxHighlighter
          language={highlightLanguage}
          style={syntaxTheme}
          showLineNumbers={showLineNumbers}
          wrapLongLines={false}
          customStyle={{
            margin: 0,
            padding: '1rem 1.5rem',
            fontSize: '0.875rem',
            borderRadius: label ? '0 0 0.75rem 0.75rem' : '0.75rem',
            overflowX: 'auto',
            maxWidth: '100%',
          }}
          codeTagProps={{
            style: {
              fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
            }
          }}
        >
          {code.trim()}
        </SyntaxHighlighter>
        {showCopy && (
          <Button
            size="sm"
            variant="secondary"
            onClick={handleCopy}
            className="absolute right-2 top-2 text-xs opacity-70 hover:opacity-100 transition-opacity"
          >
            {copied ? '✓ Copied!' : 'Copy'}
          </Button>
        )}
      </div>
    </div>
  );
}

// Simple inline code component for consistency
export function InlineCode({ children }: { children: React.ReactNode }) {
  return (
    <code className="bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded text-sm font-mono text-amber-600 dark:text-amber-400">
      {children}
    </code>
  );
}
